import React from 'react';
import { Link } from 'react-router-dom';
import './About.scss';
import Seo from '../../../components/reusable/Seo/Seo.js';
import PageHero from '../../../components/ui/PageHero.js';
import Reveal from '../../../components/ui/Reveal.js';
import { organizationSchema, breadcrumbSchema } from '../../../lib/schema.js';
import { founder } from '../../../content/team.js';
import teamPhoto from '../../../assets/images/stock/about-team.jpg';

/* ---- Data ---- */
const FACTS = [
  { t: 'Physician-owned', d: 'Owned and built by the physicians who practice in it.' },
  { t: 'Since 2021', d: 'Operating for years, not piloting.' },
  { t: 'Across Virginia', d: 'Multi-unit inpatient rehab coverage plus acute care consults.' },
  { t: 'Expanding deliberately', d: 'New markets, the same standards.' },
];

const APPROACH = [
  {
    n: '01',
    t: 'Serious medical direction',
    d: 'Written admission criteria, clinical guidelines by diagnosis group, liaison management, documentation standards, and quality metrics. Medical direction as an operating role.',
  },
  {
    n: '02',
    t: 'Standardized clinical workflows',
    d: 'The same well-designed processes on every unit we run, designed by the physicians who use them in daily practice.',
  },
  {
    n: '03',
    t: 'Software built inside the practice',
    d: 'A custom charge-capture platform and an assistive PM&R documentation tool, built in-house and used on our units daily.',
  },
  {
    n: '04',
    t: 'Performance, measured',
    d: 'Outcomes, functional gains, and program performance are measured and reviewed on a regular cadence.',
  },
];

const OWNERSHIP = [
  {
    t: 'Decisions made by people who round',
    d: 'Every call about how our units run is made by physicians who practice on them, not handed down from an office that has never seen the floor.',
  },
  {
    t: 'Workflows designed by their users',
    d: 'The people who built our admission criteria, guidelines, and software are the same physicians working inside them daily. When something doesn’t work, it gets fixed, not escalated.',
  },
  {
    t: 'Accountability without layers',
    d: 'Ownership concentrates responsibility. When you call us, you reach the people accountable for the answer.',
  },
];

/* ---- Who we are / mission ---- */
function WhoWeAre() {
  return (
    <section className="u-section about-who">
      <div className="u-container about-who__grid">
        <div className="about-who__copy">
          <Reveal>
            <span className="u-eyebrow">Who we are</span>
            <h2>A practice built to run rehab units well.</h2>
            <p className="about-who__lead">
              We exist to do one thing well: run inpatient rehabilitation the way
              it should be run. That means medical direction with real teeth,
              acute care consults that find the right patients early, and care
              optimized from admission through outcomes.
            </p>
          </Reveal>
          <dl className="about-who__facts">
            {FACTS.map((f, i) => (
              <Reveal as="div" key={f.t} className="about-who__fact" delay={0.05 * i}>
                <dt>{f.t}</dt>
                <dd>{f.d}</dd>
              </Reveal>
            ))}
          </dl>
        </div>
        <Reveal className="about-who__media" delay={0.12}>
          <img
            className="about-who__img"
            src={teamPhoto}
            alt="A rehabilitation care team moving through an inpatient unit"
            loading="lazy"
          />
        </Reveal>
      </div>
    </section>
  );
}

/* ---- Our approach (ledger) ---- */
function Approach() {
  return (
    <section className="u-section about-approach">
      <div className="u-container about-approach__grid">
        <div className="about-approach__intro">
          <Reveal>
            <span className="u-eyebrow">Our approach</span>
            <h2>
              Great rehab care should be <em>repeatable</em>, not heroic.
            </h2>
            <p className="about-approach__lead">
              Strong units don&rsquo;t run on individual effort alone. They run on
              written standards, proven workflows, and tools that make the
              right thing the easy thing on every admission and every shift.
            </p>
            <Link to="/what-we-do" className="btn btn--ghost">
              What we do <span className="btn__arrow" aria-hidden="true">→</span>
            </Link>
          </Reveal>
        </div>
        <ol className="about-approach__list">
          {APPROACH.map((a, i) => (
            <Reveal as="li" key={a.n} className="about-approach__item" delay={0.04 * i}>
              <span className="about-approach__num">{a.n}</span>
              <div>
                <h3>{a.t}</h3>
                <p>{a.d}</p>
              </div>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ---- Why physician ownership matters (dark band) ---- */
function Ownership() {
  return (
    <section className="about-own u-grain">
      <div className="u-container">
        <Reveal className="about-own__head">
          <span className="u-eyebrow u-eyebrow--on-dark">Why physician ownership matters</span>
          <h2>
            The people who own the practice are the people <em>on the unit.</em>
          </h2>
        </Reveal>
        <div className="about-own__grid">
          {OWNERSHIP.map((o, i) => (
            <Reveal as="article" key={o.t} className="about-own__point" delay={0.06 * i}>
              <h3>{o.t}</h3>
              <p>{o.d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---- Founder ---- */
function Founder() {
  return (
    <section className="u-section about-founder">
      <div className="u-container about-founder__grid">
        <Reveal className="about-founder__media" delay={0.08}>
          <figure>
            <img
              src={founder.image}
              alt={`${founder.name}, Founder of Virginia Rehab Physicians`}
            />
            <figcaption>
              <strong>{founder.name}</strong>
              <span>{founder.designation}</span>
            </figcaption>
          </figure>
        </Reveal>
        <div className="about-founder__copy">
          <Reveal>
            <span className="u-eyebrow">Founder</span>
            <h2>Built from the unit up.</h2>
            <p className="about-founder__lead">
              VRP was founded by {founder.name}, a board-certified physiatrist
              who built the practice&rsquo;s systems from years of running rehab
              units himself. Nothing here was designed in the abstract: it was
              built on units, refined on units, and is still in daily use on units.
            </p>
            <Link to="/team" className="btn btn--ghost">
              Read {founder.name.split(' ')[0]}&rsquo;s full bio{' '}
              <span className="btn__arrow" aria-hidden="true">→</span>
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---- Slim closing CTA ---- */
function ClosingCta() {
  return (
    <section className="about-cta">
      <div className="u-container about-cta__inner">
        <Reveal className="about-cta__row">
          <p>
            If this is how you want to practice, or how you want your unit run,
            let&rsquo;s talk.
          </p>
          <div className="about-cta__actions">
            <Link to="/careers" className="btn btn--gold">
              Explore joining VRP <span className="btn__arrow" aria-hidden="true">→</span>
            </Link>
            <Link to="/contact" className="btn btn--ghost">
              Get in touch
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function About() {
  const jsonLd = [
    organizationSchema(),
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'About', path: '/about' },
    ]),
  ];

  return (
    <div className="about">
      <Seo
        title="About"
        description="Virginia Rehab Physicians is a physician-owned PM&R group that medically directs and manages inpatient rehabilitation units. Operating since 2021 with multi-unit coverage across Virginia, built on written standards, proven workflows, and software made inside the practice."
        path="/about"
        jsonLd={jsonLd}
      />

      <PageHero
        eyebrow="About VRP"
        title={
          <>
            Physician-owned. Physician-built. <em>Run with care.</em>
          </>
        }
        lede="Virginia Rehab Physicians is a physician-owned PM&R group that medically directs and manages inpatient rehabilitation units inside hospitals: across Virginia today, and in new markets next."
        primary={{ label: 'Explore joining VRP', href: '/careers' }}
        secondary={{ label: 'What we do', href: '/what-we-do' }}
        tone="dark"
      />

      <WhoWeAre />
      <Approach />
      <Ownership />
      <Founder />
      <ClosingCta />
    </div>
  );
}

export default About;
